<template>
  <ClientOnly>
    <Teleport to=".filter-slide-in-target">
      <FocusTrap
        :active="filtersOpen"
        :escape-deactivates="true"
        :click-outside-deactivates="false"
      >
        <SlideIn name="FilterSlideIn">
          <template #slideInHeader="{ toggle: _toggle }">
            <div
              class="flex w-full items-center justify-between"
              :class="{ 'border-b border-b-platin px-4': isMobile }"
            >
              <Headline :is-uppercase="false" size="lg" tag="p"
                >{{ $t('filter.title') }}
              </Headline>
              <Button
                type="ghost"
                class="pr-0"
                :class="{ '-mr-2': isMobile }"
                :aria-label="$t('dialog.close')"
                @click="_toggle"
              >
                <template #icon>
                  <IconCloseS class="size-8" />
                </template>
              </Button>
            </div>
          </template>
          <template #slideInBody>
            <div class="border-b-8 border-b-platin sm:hidden">
              <MobileSortingMenu :values="values" />
            </div>
            <div
              v-if="filterGroups.lined.length > 0"
              class="mx-4 mb-4 flex flex-row"
            >
              <template v-for="filter in filterGroups.lined" :key="filter.slug">
                <BooleanFilter
                  v-if="filter.type === 'boolean'"
                  :id="filter.name"
                  :filter="filter"
                  :model-value="localAppliedBooleanValues[filter.slug] ?? false"
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value="
                    applyBooleanFilterToLocalState(filter.slug)
                  "
                />

                <LazyRangeFilter
                  v-else-if="filter.type === 'range'"
                  :id="filter.name"
                  :filter="filter"
                  :min-value="filter.values[0].min"
                  :max-value="filter.values[0].max"
                  :model-value="
                    getPriceRange(filter.values[0].min, filter.values[0].max)
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value="
                    createPriceFilterHandler(
                      filter.values[0].min,
                      filter.values[0].max,
                    )($event)
                  "
                  @reset="resetPriceFilter"
                />

                <ColorFilter
                  v-else-if="filter.slug === 'colorRange'"
                  :id="filter.name"
                  :filter="filter"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />

                <SizeFilter
                  v-else-if="filter.slug === 'size'"
                  :id="filter.name"
                  :filter="filter"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />
                <SizeCategoryFilter
                  v-else-if="filter.slug === 'sizeCategorySize'"
                  :id="filter.name"
                  :filter="filter"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  :ungrouped-filter-values="filter.values"
                  :size-category-sort-order-override="sizeCategorySortOrder"
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />

                <AttributesFilter
                  v-else
                  :id="filter.name"
                  :filter="filter"
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />
              </template>
            </div>
            <div class="flex flex-col" :class="{ 'gap-6': !isMobile }">
              <template
                v-for="filter in filterGroups.stacked"
                :key="filter.slug"
              >
                <BooleanFilter
                  v-if="filter.type === 'boolean'"
                  :id="filter.name"
                  :is-collapsible="filter.isCollapsible"
                  :filter="filter"
                  :model-value="localAppliedBooleanValues[filter.slug] ?? false"
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value="
                    applyBooleanFilterToLocalState(filter.slug)
                  "
                />

                <LazyRangeFilter
                  v-else-if="filter.type === 'range'"
                  :id="filter.name"
                  :filter="filter"
                  :min-value="filter.values[0].min"
                  :max-value="filter.values[0].max"
                  :is-collapsible="filter.isCollapsible"
                  :model-value="
                    getPriceRange(filter.values[0].min, filter.values[0].max)
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value="
                    createPriceFilterHandler(
                      filter.values[0].min,
                      filter.values[0].max,
                    )($event)
                  "
                  @reset="resetPriceFilter"
                />

                <ColorFilter
                  v-else-if="filter.slug === 'colorRange'"
                  :id="filter.name"
                  :filter="filter"
                  :is-collapsible="filter.isCollapsible"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />

                <SizeFilter
                  v-else-if="filter.slug === 'size'"
                  :id="filter.name"
                  :filter="filter"
                  :is-collapsible="filter.isCollapsible"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />
                <SizeCategoryFilter
                  v-else-if="filter.slug === 'sizeCategorySize'"
                  :id="filter.name"
                  :filter="filter"
                  :is-collapsible="filter.isCollapsible"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  :ungrouped-filter-values="filter.values"
                  :size-category-sort-order-override="sizeCategorySortOrder"
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />

                <AttributesFilter
                  v-else
                  :id="filter.name"
                  :filter="filter"
                  :is-collapsible="filter.isCollapsible"
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />
              </template>
            </div>
          </template>

          <template #slideInActions>
            <div
              class="flex flex-col sm:flex-row"
              :class="{ 'gap-4': !isMobile }"
            >
              <Button
                type="tertiary"
                :class="{ 'mx-4 mb-2 mt-4 bg-white': isMobile }"
                @click="resetAllFilters"
              >
                {{ $t('filter.delete') }}
              </Button>
              <Button
                :class="{ 'mx-4 mb-4 mt-2': isMobile }"
                data-test-id="apply-filter-button"
                :is-full-width="!isMobile"
                type="primary"
                @click="applyFilters"
              >
                {{ showResultText + $t('filter.show_result') }}
              </Button>
            </div>
          </template>
        </SlideIn>
      </FocusTrap>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import type {
  CentAmount,
  ProductFilter,
  ProductSearchQuery,
  TransformedFilter,
} from '@scayle/storefront-nuxt'
import { getGroupedFilterableValues } from '@scayle/storefront-nuxt'
import { FocusTrap } from 'focus-trap-vue'
import type { RangeTuple } from '../ui/sliders/RangeSlider.vue'
import SlideIn from '#layers/base/components/layout/slideIns/SlideIn.vue'
import Headline from '#layers/base/components/ui/headlines/Headline.vue'
import AttributesFilter from '~/components/filters/type/AttributesFilter.vue'
import ColorFilter from '~/components/filters/type/ColorFilter.vue'
import BooleanFilter from '#layers/base/components/filters/type/BooleanFilter.vue'
import SizeFilter from '#layers/base/components/filters/type/SizeFilter.vue'
import SizeCategoryFilter from '~/components/filters/type/SizeCategoryFilter.vue'
import MobileSortingMenu from '~/components/sorting/MobileSortingMenu.vue'
import type { FilterItemWithValues } from '#layers/base/types/filter'
import { useAppliedFilters } from '#layers/base/composables/useAppliedFilters'
import { fromEntries } from '#layers/base/utils/object'

type FilterSlideInProps = {
  category?: string
  filters?: FilterItemWithValues[]
  appliedFilter?: ProductSearchQuery
  appliedBooleanValues?: Record<string, boolean>
  appliedAttributeValues?: Record<string, number[]>
  values: SortValue[]
  sizeCategorySortOrder?: string | string[] | null
}

const props = withDefaults(defineProps<FilterSlideInProps>(), {
  category: '',
  filters: () => [],
  appliedFilter: () => ({}) as ProductSearchQuery,
  appliedBooleanValues: () => ({}),
  appliedAttributeValues: () => ({}),
  sizeCategorySortOrder: null,
})

const emit = defineEmits<{
  applyFilters: [filters: Record<string, any>]
  resetFilters: []
}>()

const { filters: allAvailableFilters } = toRefs(props)
const { toggle, isOpen: filtersOpen } = useSlideIn('FilterSlideIn')
const route = useRoute()
const { isMobile } = useDeviceType()

// Adapt the route query to the local state format
// So for example, comma-separated values are converted to arrays
const routeEntries = Object.entries(route.query).map(
  ([key, value]): [string, string | string[] | null] => {
    if (typeof value === 'string' && value?.indexOf(',') !== -1) {
      return [key, value.split(',')]
    }
    if (value) {
      return [key, Array.isArray(value) ? value.join(',') : value.toString()]
    }
    return [key, value]
  },
)
const adaptedRouteQuery = fromEntries(
  routeEntries.filter(
    (e): e is [string, string | string[]] => e[1] != null && e[1] !== '',
  ),
) as Record<string, string | string[] | boolean>
const localState = ref(adaptedRouteQuery)
const justCleared = ref(false)

// Watch for sort parameter changes from mobile sorting and sync to local state
watch(
  () => route.query.sort,
  (newSort) => {
    if (newSort && newSort !== localState.value.sort) {
      localState.value.sort = newSort.toString()
    } else if (!newSort && localState.value.sort) {
      delete localState.value.sort
    }
  },
  { immediate: true },
)

const localStateAsRouteQuery = computed(() => {
  return Object.fromEntries(
    Object.entries(localState.value).map(([key, value]) => {
      return [key, Array.isArray(value) ? value.join(',') : value.toString()]
    }),
  )
})

const localAppliedFilters = computed(() =>
  useAppliedFilters(localStateAsRouteQuery.value),
)

const localAppliedBooleanValues = computed(() => {
  return localAppliedFilters.value.appliedBooleanValues.value
})

const productCounts = useProductsCount(
  {
    params: computed(() => ({
      category: props.category,
      includeSoldOut: false,
      includedSellableForFree: true,
      where: localAppliedFilters.value.appliedFilter.value,
    })),
    options: { immediate: true },
  },
  `useProductsCount-${props.category}`,
)

const supportedFilters = computed(() => {
  const result = [
    'prices',
    'sale',
    'isNew',
    ...allAvailableFilters.value
      .filter((f: ProductFilter) => f.type === 'attributes')
      .filter((f: ProductFilter) => f.slug !== 'color')
      .map((f: ProductFilter) => f.slug),
  ]
  // HINT: There are 2 size fiters existing with slugs 'sizeCategorySize' and 'size'
  // sometimes both of them are returned from backend.
  // Only one should be displayed and 'sizeCategorySize' should always take precedence
  if (result.includes('sizeCategorySize')) {
    const i = result.indexOf('size')
    if (i > -1) {
      result.splice(i, 1)
    }
  }
  return result
})

const filtersPresentationOrder: Record<string, number> = {
  isNew: 0,
  sale: 1,
  colorRange: 2,
  prices: 3,
  sizeCategorySize: 4,
  size: 5,
}
const mobileExpandedFilters = ['isNew', 'sale']
const generalExpandedFilters = [
  ...mobileExpandedFilters,
  'colorRange',
  'prices',
  'sizeCategorySize',
  'size',
]

const getFilterValuesForAppliedAttributeValues = (
  slug: string,
  values?: string | string[] | boolean,
) => {
  const filter = availableFilterValues.value[slug]
  if (!filter || !values) {
    return []
  }
  const valuesArray = Array.isArray(values) ? values : [values]
  return filter.filter((v: any) => valuesArray.includes(v.value.toString()))
}
const isFilterCollapsible = (filterSlug: string) => {
  return isMobile.value
    ? !mobileExpandedFilters.includes(filterSlug)
    : !generalExpandedFilters.includes(filterSlug)
}

const getFilterPresentationPosition = (filterSlug: string) =>
  filtersPresentationOrder[filterSlug] ?? 100

const { t, te } = useI18n()
const translateFilterName = (filter: FilterItemWithValues) => {
  const key = `filter.${filter.slug}`
  return te(key) ? t(key) : filter.name
}

type CollapsibleFilter = FilterItemWithValues & { isCollapsible: boolean }

const allowedFilters = computed(() => {
  return allAvailableFilters.value
    .reduce((acc: CollapsibleFilter[], f) => {
      if (supportedFilters.value.includes(f.slug)) {
        acc.push({
          ...f,
          name: translateFilterName(f),
          isCollapsible: isFilterCollapsible(f.slug),
        })
      }
      return acc
    }, [])
    .sort(
      (a: CollapsibleFilter, b: CollapsibleFilter) =>
        getFilterPresentationPosition(a.slug) -
        getFilterPresentationPosition(b.slug),
    )
})

const filterGroups = computed(() => {
  return allowedFilters.value.reduce(
    (
      acc: { stacked: CollapsibleFilter[]; lined: CollapsibleFilter[] },
      f: CollapsibleFilter,
    ) => {
      if (isMobile.value && !f.isCollapsible) {
        acc.lined.push(f)
      } else {
        acc.stacked.push(f)
      }
      return acc
    },
    { stacked: [], lined: [] },
  )
})

const availableFilterValues = computed(() =>
  getGroupedFilterableValues(supportedFilters.value, allowedFilters.value),
)
const emptyFilterValues: TransformedFilter[] = []
const getAvailableFilterValues = (slug: string) =>
  availableFilterValues.value[slug] ?? emptyFilterValues

const applyFilters = () => {
  emit('applyFilters', localStateAsRouteQuery.value)
  toggle()
}

const showResultText = computed(() =>
  productCounts.data &&
  productCounts.data.value?.count &&
  productCounts.data.value?.count >= 0
    ? `${productCounts.data.value?.count} `
    : '',
)

const getPriceRange = (
  currentMin: CentAmount,
  currentMax: CentAmount,
): RangeTuple => {
  const minPrice = Array.isArray(localState.value.minPrice)
    ? localState.value.minPrice[0]
    : localState.value.minPrice
  const maxPrice = Array.isArray(localState.value.maxPrice)
    ? localState.value.maxPrice[0]
    : localState.value.maxPrice

  const appliedMin =
    minPrice && typeof minPrice === 'string' ? parseInt(minPrice) : currentMin
  const appliedMax =
    maxPrice && typeof maxPrice === 'string' ? parseInt(maxPrice) : currentMax

  const min = Math.max(currentMin, appliedMin ?? currentMin, 0)
  const max = Math.min(currentMax, appliedMax ?? currentMax)

  return [min, max]
}

const applyAttributeFilterToLocalState = (slug: string, values: number[]) => {
  if (Array.isArray(values)) {
    if (values.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete localState.value[slug]
    } else {
      localState.value[slug] = values.map((v) => v.toString())
    }
  }
}

const applyBooleanFilterToLocalState = (slug: string) => {
  // If the filter is already applied, remove it
  if (Object.keys(localState.value).includes(slug)) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete localState.value[slug]
  } else {
    localState.value[slug] = 'true'
  }
}

const applyPriceFilterToLocalState = (
  prices: RangeTuple,
  currentMin?: CentAmount,
  currentMax?: CentAmount,
) => {
  // Skip applying price filters immediately after calling resetAllFilters to prevent default ranges to apply as filters
  if (justCleared.value) {
    return
  }

  // Don't apply price filters if they match the default range (no actual filtering)
  if (
    currentMin !== undefined &&
    currentMax !== undefined &&
    prices[0] === currentMin &&
    prices[1] === currentMax
  ) {
    // Remove any existing price filters since we're back to default
    delete localState.value.minPrice
    delete localState.value.maxPrice
    return
  }

  localState.value.minPrice = prices[0].toString()
  localState.value.maxPrice = prices[1].toString()
}

const createPriceFilterHandler = (
  currentMin: CentAmount,
  currentMax: CentAmount,
) => {
  return (prices: RangeTuple) =>
    applyPriceFilterToLocalState(prices, currentMin, currentMax)
}

const resetPriceFilter = () => {
  delete localState.value.minPrice
  delete localState.value.maxPrice
}

const resetFilter = (slug: string) => {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete localState.value[slug]
}

const resetAllFilters = () => {
  justCleared.value = true
  localState.value = {}
  emit('applyFilters', { sort: undefined })
  justCleared.value = false
}
</script>
